import { useEffect, useState } from 'react'
import {
  AudioMutedOutlined,
  AudioOutlined,
  CameraOutlined,
  PhoneFilled,
  RetweetOutlined,
  SoundOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { releaseOutgoingCall, resolveOutgoingCallDeadlineMs } from '@/lib/outgoingCallLifecycle'
import { getAvatarUrl } from '@/lib/image'
import { getEnvConfig } from '@/lib/runtime-env'
import { defaultCallSessionMediaPreferences, useCallStore } from '@/store/callStore'
import { CallSurface } from './CallSurface'
import { CallSurfaceTopBar } from './CallSurfaceTopBar'
import { CallUserProfile } from './CallUserProfile'

function formatCountdown(seconds: number) {
  const safeSeconds = Math.max(0, seconds)
  const minutes = Math.floor(safeSeconds / 60)
  const rest = safeSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

export function OutgoingCallView() {
  const outgoingCall = useCallStore((state) => state.outgoingCall)
  const currentCall = useCallStore((state) => state.currentCall)
  const sessionMediaPreferences = useCallStore((state) => state.sessionMediaPreferences)
  const setSessionMediaPreferences = useCallStore((state) => state.setSessionMediaPreferences)
  const [isCompact, setIsCompact] = useState(false)
  const [canceling, setCanceling] = useState(false)
  const [deadlineMs, setDeadlineMs] = useState(() => resolveOutgoingCallDeadlineMs())
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    Math.max(0, Math.ceil((resolveOutgoingCallDeadlineMs() - Date.now()) / 1000))
  )

  const callerName = outgoingCall?.nickname || 'Calling'
  const outgoingCallKey = outgoingCall?.order_id || outgoingCall?.call_id || ''
  const avatarUrl = getAvatarUrl(outgoingCall?.avatar, getEnvConfig().CDN_URL)
  const mediaPreferences =
    sessionMediaPreferences?.orderID === outgoingCallKey
      ? sessionMediaPreferences
      : { orderID: outgoingCallKey, ...defaultCallSessionMediaPreferences }
  const mediaControlsDisabled = canceling || outgoingCall?.status === 'accepted'
  const microphoneOn = mediaPreferences.localAudioEnabled
  const speakerOn = mediaPreferences.remoteAudioEnabled
  const cameraOn = mediaPreferences.localVideoEnabled

  useEffect(() => {
    if (!outgoingCall) {
      return
    }
    const nextDeadlineMs = resolveOutgoingCallDeadlineMs(outgoingCall.expire_at)
    setIsCompact(false)
    setCanceling(false)
    setDeadlineMs(nextDeadlineMs)
    setRemainingSeconds(Math.max(0, Math.ceil((nextDeadlineMs - Date.now()) / 1000)))
  }, [outgoingCallKey, outgoingCall])

  useEffect(() => {
    if (!outgoingCall) {
      return
    }
    const updateRemaining = () => {
      setRemainingSeconds(Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000)))
    }
    updateRemaining()
    const timer = window.setInterval(updateRemaining, 250)
    return () => window.clearInterval(timer)
  }, [deadlineMs, outgoingCall])

  if (!outgoingCall || currentCall) {
    return null
  }

  const cancelCall = async () => {
    if (canceling || !outgoingCall.order_id) {
      return
    }
    setCanceling(true)
    try {
      await releaseOutgoingCall(outgoingCall.order_id, 'call_canceled', 'anchor_cancel', {
        notify: true,
        warning: 'Call canceled',
      })
    } finally {
      setCanceling(false)
    }
  }

  const controls = (
    <>
      <div className="outgoing-call-controls">
        <button
          className="outgoing-call-control"
          disabled={mediaControlsDisabled}
          type="button"
          onClick={() =>
            setSessionMediaPreferences(outgoingCallKey, { localAudioEnabled: !mediaPreferences.localAudioEnabled })}
        >
          {microphoneOn ? <AudioOutlined /> : <AudioMutedOutlined />}
          <span>{microphoneOn ? '麦克风已开' : '麦克风已关'}</span>
        </button>
        <button
          className="outgoing-call-control"
          disabled={mediaControlsDisabled}
          type="button"
          onClick={() =>
            setSessionMediaPreferences(outgoingCallKey, { remoteAudioEnabled: !mediaPreferences.remoteAudioEnabled })}
        >
          <SoundOutlined />
          <span>{speakerOn ? '扬声器已开' : '扬声器已关'}</span>
        </button>
        <button
          className="outgoing-call-control"
          disabled={mediaControlsDisabled}
          type="button"
          onClick={() =>
            setSessionMediaPreferences(outgoingCallKey, { localVideoEnabled: !mediaPreferences.localVideoEnabled })}
        >
          {cameraOn ? <VideoCameraOutlined /> : <CameraOutlined />}
          <span>{cameraOn ? '摄像头已开' : '摄像头已关'}</span>
        </button>
      </div>

      <button
        className="outgoing-call-hangup"
        disabled={canceling}
        type="button"
        aria-label="Cancel call"
        onClick={() => void cancelCall()}
      >
        <PhoneFilled />
      </button>

      <button className="outgoing-call-switch-camera" type="button" aria-label="Switch camera">
        <RetweetOutlined />
      </button>
    </>
  )

  return (
    <CallSurface
      ariaLabel="Outgoing video call"
      compact={isCompact}
      mediaImage={avatarUrl}
      top={
        <CallSurfaceTopBar
          compact={isCompact}
          center={
            <div className="outgoing-call-countdown" aria-live="polite">
              {formatCountdown(remainingSeconds)}
            </div>
          }
          summaryUserID={outgoingCall.user_id || outgoingCall.from_user_id}
          onToggleCompact={() => setIsCompact((value) => !value)}
        />
      }
      profile={<CallUserProfile avatar={outgoingCall.avatar} name={callerName} subtitle="Calling..." />}
      controls={controls}
      variant="outgoing"
    />
  )
}
